// web/src/lib/apiBase.ts

function trimTrailingSlash(value: string) {
  return value.replace(/\/+$/, "");
}

function normalizePath(path: string) {
  if (!path) return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

function isAbsoluteHttpUrl(value: string) {
  return value.startsWith("http://") || value.startsWith("https://");
}

function isNgrokUrl(value: string) {
  try {
    const host = new URL(value).hostname.toLowerCase();
    return host.includes("ngrok") || host.includes("ngrok-free.app");
  } catch {
    return value.toLowerCase().includes("ngrok");
  }
}

function inferProdApiBaseFromHost(host: string) {
  // host like: vetcan.astormscoming.com -> api.vetcan.astormscoming.com
  // If host already starts with api., keep it.
  const normalizedHost = host.trim().toLowerCase();
  if (!normalizedHost) return "";
  if (normalizedHost === "studio.astormscoming.com") {
    return "https://api.vetcan.astormscoming.com";
  }
  if (normalizedHost.startsWith("api.")) return `https://${normalizedHost}`;
  return `https://api.${normalizedHost}`;
}

export function resolveVetcanApiBaseUrl(options?: {
  envBaseUrl?: string;
  legacyEnvBaseUrl?: string;
  isDev?: boolean;
  isProd?: boolean;
  origin?: string;
  host?: string;
}) {
  const isDev = !!options?.isDev;
  const isProd = !!options?.isProd;

  const envPrimary = options?.envBaseUrl?.trim() || "";
  const envLegacy = options?.legacyEnvBaseUrl?.trim() || "";

  // 1) Prefer explicit env base
  if (envPrimary) {
    const normalized = trimTrailingSlash(envPrimary);
    if (isProd && isNgrokUrl(normalized)) {
      // never ship ngrok in prod; fall through to inferred prod base
    } else {
      return normalized;
    }
  }

  // 2) Support legacy/mistyped env var
  if (envLegacy) {
    const normalized = trimTrailingSlash(envLegacy);
    if (isProd && isNgrokUrl(normalized)) {
      // never ship ngrok in prod; fall through
    } else {
      return normalized;
    }
  }

  // 3) In production, infer api.<host> so we never accidentally use same-origin Hostinger /api/*
  if (isProd) {
    const host = options?.host?.trim() || "";
    const inferred = inferProdApiBaseFromHost(host);
    if (inferred) return inferred;

    // last-resort: if host missing but origin exists, infer from origin
    const origin = trimTrailingSlash(options?.origin || "");
    if (origin) {
      try {
        const o = new URL(origin);
        const inferredFromOrigin = inferProdApiBaseFromHost(o.host);
        if (inferredFromOrigin) return inferredFromOrigin;
      } catch {
        // ignore
      }
    }
  }

  // 4) Dev fallback
  if (isDev) return "http://localhost:4000";

  // 5) Final fallback: empty means caller may use same-origin/proxy intentionally
  return "";
}

export function getVetcanApiBaseUrl(): string {
  return resolveVetcanApiBaseUrl({
    envBaseUrl: import.meta.env.VITE_VETCAN_API_BASE_URL,
    legacyEnvBaseUrl: (import.meta.env as any).VITE_API_BASE,
    isDev: import.meta.env.DEV,
    isProd: import.meta.env.PROD,
    origin: typeof window !== "undefined" ? window.location.origin : "",
    host: typeof window !== "undefined" ? window.location.host : "",
  });
}

export function withVetcanApiBase(input: RequestInfo | URL) {
  const base = getVetcanApiBaseUrl();
  if (!base) return input;

  if (input instanceof URL) return input;

  if (typeof input === "string") {
    if (isAbsoluteHttpUrl(input)) return input;

    // We only rewrite absolute-path requests ("/api/...") or normalized later.
    if (!input.startsWith("/")) return input;

    // If base equals same-origin *and* you intentionally want relative calls, keep relative.
    // In production, base will generally be https://api.<host>, not window.origin.
    if (typeof window !== "undefined" && base === window.location.origin) {
      return input;
    }

    return `${trimTrailingSlash(base)}${input}`;
  }

  return input;
}

export function vetcanUrl(path: string): string {
  if (isAbsoluteHttpUrl(path)) return path;

  const normalizedPath = normalizePath(path);
  const base = getVetcanApiBaseUrl();

  if (!base) return normalizedPath;

  if (typeof window !== "undefined" && base === window.location.origin) {
    return normalizedPath;
  }

  // base should be absolute in nearly all cases when set
  if (isAbsoluteHttpUrl(base)) {
    return new URL(normalizedPath, `${trimTrailingSlash(base)}/`).toString();
  }

  return `${base}${normalizedPath}`;
}
